// REACT, STYLE, STORIES & COMPONENT
import React from 'react';
import styles from './ConfigurationError.module.scss';

// 3RD PARTY
import { useHistory } from 'react-router';
import classNames from 'classnames';

// ASSETS
import { IconsSvg } from 'assets/icons';
import { NETWORK_ERROR } from 'api/apisauce';

// OTHER COMPONENTS
// eslint-disable-next-line import/no-cycle
import { Button, OverlayModal, SkeletonFlexible } from 'ui/basic';
import { StandaloneHeader } from 'features/framework/components/MainLayout/StandaloneWrapper/StandaloneHeader';

// UTILS
import { useTranslate } from 'utils/translator';
import { markdown } from 'utils/textTools';
import { SUPPORT_LINK } from 'utils/configuration';
import * as localStorage from 'utils/localStorage';

// STORE
import { Provider, useSelector } from 'react-redux';
import store from 'store';
import { selectSettingsTranslations } from 'features/framework/storeNext/configurationSlice';


const Wrapper = (props) => {
  const { isLoggedIn, children, onClose } = props;

  const translate = useTranslate();

  if (!isLoggedIn) {
    return (
      <div className={styles.overlay}>
        <Provider store={store}>
          <StandaloneHeader />
        </Provider>
        { children }
      </div>
    );
  }

  return (
    <OverlayModal
      header={translate('assessment_registration_error')}
      hasHighestVisibility
      onClose={onClose}
    >
      { children }
    </OverlayModal>
  );
};


// COMPONENT: ConfigurationError
// Note: for text, always include English key fallbacks (in case of network errors).
const ConfigurationError = (props) => {
  // PROPS
  const { error } = props;

  const history = useHistory();
  const translate = useTranslate();

  const translations = useSelector(selectSettingsTranslations);
  const translationsLoaded = Boolean(Object.keys(translations ?? {}).length);

  const searchParams = new URLSearchParams(window.location.search);
  let message = error?.message || error?.problem;
  if (searchParams.get('messageKey')) {
    message = translate(searchParams.get('messageKey'));
  }
  const isNetworkError = message === NETWORK_ERROR;

  let errorDetails = JSON.stringify(error?.body, null, 2);
  if (searchParams.get('errorDetails')) {
    errorDetails = searchParams.get('errorDetails');
  }

  const isLoggedIn = localStorage.getItem('token');
  const isErrorRoute = window.location.pathname.includes('/error');

  const handleRetry = () => {
    localStorage.logout();
    window.location.reload();
  };

  if (!isErrorRoute && (!error || error.nonexistent)) {
    return null;
  }


  // RENDER: ConfigurationError
  return (
    <Wrapper
      isLoggedIn={isLoggedIn}
      onClose={isErrorRoute ? () => history.push('/') : undefined}
    >
      <div className={styles.configurationError}>
        <div className={styles.content}>
          { !translationsLoaded && !isNetworkError
            ? <SkeletonFlexible looks='centered' />
            : (
              <>
                <div className='bluTypeXl' role='img' aria-label='applause'>
                  &#128546;
                </div>
                <div className={classNames('bluTypeS', 'marginTopXs')}>
                  { translate('config_error_title') || 'Oh no! Something went wrong.' }
                </div>
                <div className={classNames('bluTypeCopy', 'marginTopXs')}>
                  { isNetworkError
                    ? markdown(`[blu-markdown]\nWe are very sorry, but it seems like an error occurred here. Please try again in a few minutes. If you keep seeing this page, reach out to the [bluquist support](${SUPPORT_LINK}) with the details of the error.`) // eslint-disable-line max-len
                    : message || translate('config_error_copy', [ '{{link}}', SUPPORT_LINK ]) }
                </div>

                { errorDetails && (
                  <div className={styles.errorDetails}>
                    <span className='bluTypeXxs'>
                      { translate('error_details') || 'Error details' }
                      :
                    </span>
                    <div className={styles.box}>
                      <samp>{ errorDetails }</samp>
                    </div>
                  </div>
                ) }

                <div className={styles.controls}>
                  { isLoggedIn && (
                    <>
                      { errorDetails && (
                        <Button
                          looks='secondary'
                          leadingIcon={IconsSvg.Copy}
                          onClick={() => navigator.clipboard.writeText(errorDetails)}
                        >
                          { translate('copy_error_details') || 'Copy error details' }
                        </Button>
                      ) }

                      { isErrorRoute && (
                        <Button
                          leadingIcon={IconsSvg.CloseBig}
                          onClick={() => history.push('/')}
                        >
                          { translate('close_lbl') || 'Close' }
                        </Button>
                      ) }

                      { !isErrorRoute && (
                        <Button
                          looks={isNetworkError ? 'primary' : 'secondary'}
                          leadingIcon={IconsSvg.Loop}
                          onClick={handleRetry}
                        >
                          { translate('retry') || 'Retry' }
                        </Button>
                      ) }
                    </>
                  ) }

                  { !isLoggedIn && (
                    <>
                      { errorDetails && (
                        <Button
                          leadingIcon={IconsSvg.Copy}
                          onClick={() => navigator.clipboard.writeText(errorDetails)}
                        >
                          { translate('copy_error_details') || 'Copy error details' }
                        </Button>
                      ) }

                      { !isErrorRoute && (
                        <Button
                          looks={isNetworkError ? 'primary' : 'secondary'}
                          leadingIcon={IconsSvg.Loop}
                          onClick={handleRetry}
                        >
                          { translate('retry') || 'Retry' }
                        </Button>
                      ) }
                    </>
                  ) }
                </div>
              </>
            ) }
        </div>
      </div>
    </Wrapper>
  );
};

export default ConfigurationError;
